/**
 * TrackingRewardsSettings - Admin page for configuring tracking rewards
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Coins, ArrowLeft, Save, Plus, Trash2, Info, 
  Users, TrendingUp, Award, Settings
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TrackingRewardsSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [studioId, setStudioId] = useState(null);
  
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get current studio
      const meRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentStudioId = meRes.data.studio_id || 'default';
      setStudioId(currentStudioId);
      
      // Load config and stats
      const [configRes, statsRes] = await Promise.all([
        axios.get(`${API}/portal/nutrition/admin/tracking-rewards/config/${currentStudioId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/portal/nutrition/admin/tracking-rewards/stats/${currentStudioId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/portal/nutrition/admin/tracking-rewards/config/${studioId}`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Einstellungen gespeichert!');
      } else {
        toast.error(response.data.error || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addTier = () => {
    const newTiers = [...(config.tiers || [])];
    const lastTier = newTiers[newTiers.length - 1];
    newTiers.push({
      name: `Stufe ${newTiers.length + 1}`,
      days_required: lastTier ? lastTier.days_required + 2 : 3,
      points_reward: lastTier ? lastTier.points_reward + 3 : 2
    });
    setConfig({ ...config, tiers: newTiers });
  };

  const removeTier = (index) => {
    const newTiers = config.tiers.filter((_, i) => i !== index);
    setConfig({ ...config, tiers: newTiers });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...config.tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setConfig({ ...config, tiers: newTiers });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/portal-settings')}
          className="p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Coins className="w-6 h-6 text-yellow-500" />
            Tracking-Punkte Einstellungen
          </h1>
          <p className="text-gray-600">
            Belohnungen für regelmässiges Ernährungs-Tracking konfigurieren
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Statistics Card */}
        {stats && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Statistiken
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{stats.total_points_awarded || 0}</p>
                  <p className="text-sm text-gray-600">Punkte vergeben</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{stats.unique_customers || 0}</p>
                  <p className="text-sm text-gray-600">Aktive Nutzer</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{stats.total_claims || 0}</p>
                  <p className="text-sm text-gray-600">Belohnungen</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{stats.this_week_claims || 0}</p>
                  <p className="text-sm text-gray-600">Diese Woche</p>
                </div>
              </div>
              
              {stats.tier_breakdown && Object.keys(stats.tier_breakdown).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-600 mb-2">Stufen-Verteilung:</p>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(stats.tier_breakdown).map(([tier, count]) => (
                      <Badge key={tier} variant="secondary">
                        {tier}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Grundeinstellungen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Tracking-Punkte aktivieren</Label>
                <p className="text-sm text-gray-500">
                  Kunden können durch Tracking Punkte verdienen
                </p>
              </div>
              <Switch
                checked={config?.enabled ?? true}
                onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
              />
            </div>

            {/* Calorie Tolerance */}
            <div className="space-y-2">
              <Label>Kalorien-Toleranz (±)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={config?.calorie_tolerance || 600}
                  onChange={(e) => setConfig({ ...config, calorie_tolerance: parseInt(e.target.value) })}
                  className="w-32"
                />
                <span className="text-gray-600">kcal</span>
              </div>
              <p className="text-sm text-gray-500">
                Ein Tag zählt als "getrackt", wenn die Kalorien innerhalb dieser Toleranz vom Ziel sind
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Beschreibung (für Kunden sichtbar)</Label>
              <Input
                value={config?.description || ''}
                onChange={(e) => setConfig({ ...config, description: e.target.value })}
                placeholder="Tracke deine Ernährung und verdiene Punkte!"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tiers Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Belohnungs-Stufen
              </div>
              <Button variant="outline" size="sm" onClick={addTier}>
                <Plus className="w-4 h-4 mr-1" />
                Stufe hinzufügen
              </Button>
            </CardTitle>
            <CardDescription>
              Definieren Sie, wie viele Tage für welche Punktzahl benötigt werden
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {config?.tiers?.map((tier, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Name</Label>
                      <Input
                        value={tier.name}
                        onChange={(e) => updateTier(index, 'name', e.target.value)}
                        placeholder="Bronze"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Tage benötigt</Label>
                      <Input
                        type="number"
                        min="1"
                        value={tier.days_required}
                        onChange={(e) => updateTier(index, 'days_required', parseInt(e.target.value))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Punkte</Label>
                      <Input
                        type="number"
                        min="1"
                        value={tier.points_reward}
                        onChange={(e) => updateTier(index, 'points_reward', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTier(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    disabled={config.tiers.length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Preview */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-yellow-600" />
                <span className="font-medium text-yellow-800">Vorschau für Kunden</span>
              </div>
              <div className="flex gap-4 flex-wrap">
                {config?.tiers?.map((tier, idx) => (
                  <div key={idx} className="text-center">
                    <Badge className="bg-yellow-500 text-white">{tier.name}</Badge>
                    <p className="text-xs text-gray-600 mt-1">
                      {tier.days_required} Tage → {tier.points_reward}P
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={saveConfig}
            disabled={saving}
            className="bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            {saving ? (
              <span className="animate-spin mr-2">⏳</span>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Einstellungen speichern
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrackingRewardsSettings;
